import { useState } from "react";
import { Box } from "@mui-wrapper";
import { Dialog, Text, TextField, useToast } from "@flat-ui";
import { AdmPlan } from "@custom-types/base-types";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppDispatch } from "@store/store-helper";
import { deletePlan, updatePlan } from "@store/plans/plans-thunks";
import { formatPlanName } from "@utils/data-display";
import {
  BULK_PLAN_ACTION_TEXTS,
  BulkPlanAction,
  getDeactivationComment,
  isDeletionConfirmed,
} from "@utils/plan-actions-utils";
import { DeletePlanConfirmation } from "@components/plans-table/delete-plan-confirmation";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";

interface Props {
  /** The action to apply to all the plans */
  action: BulkPlanAction;

  /** The plans the action is applied to */
  plans: AdmPlan[];

  /** Function called once the action was applied, whether it failed for some plans or not */
  onApplied(): void;

  /** Function called when the user closes the dialog */
  onClose(): void;
}

/**
 * Applies the action to one plan after the other, so that the backend is not flooded with requests,
 * and carries on when it fails for one of them
 *
 * @returns the errors of the plans the action failed for
 */
async function applyToEachPlan(
  plans: AdmPlan[],
  apply: (plan: AdmPlan) => Promise<unknown>
): Promise<unknown[]> {
  const errors: unknown[] = [];

  for (const plan of plans) {
    try {
      await apply(plan);
    } catch (error) {
      errors.push(error);
    }
  }

  return errors;
}

/** Asks to confirm the action, naming the plans "this plan" or "these N plans" */
function getConfirmationText(
  action: BulkPlanAction,
  planCount: number
): string {
  const isSinglePlan = planCount === 1;

  if (action === "delete") {
    return isSinglePlan
      ? "Deleting this plan will remove its usage from the list. This cannot be undone."
      : `Deleting these ${planCount} plans will remove their usage from the list. This cannot be undone.`;
  }

  return isSinglePlan
    ? "Are you sure you want to deactivate this plan?"
    : `Are you sure you want to deactivate these ${planCount} plans?`;
}

/** Dialog to confirm deactivating or deleting several plans at once */
export function BulkPlanActionDialog({
  action,
  plans,
  onApplied,
  onClose,
}: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandler();
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [deactivationReason, setDeactivationReason] = useState<string>("");
  const [confirmationText, setConfirmationText] = useState<string>("");

  const { infinitive, pastParticiple } = BULK_PLAN_ACTION_TEXTS[action];
  const planCountText = `${plans.length} ${plans.length === 1 ? "plan" : "plans"}`;

  async function applyToPlan(plan: AdmPlan): Promise<void> {
    if (action === "delete") {
      await dispatch(deletePlan({ planId: plan.id })).unwrap();
      return;
    }

    await dispatch(
      updatePlan({
        planId: plan.id,
        updatedProps: {
          type: "deactivating",
          deactivatedComment: getDeactivationComment({
            planComment: plan.description ?? "",
            reason: deactivationReason,
          }),
        },
      })
    ).unwrap();
  }

  async function handleConfirm(): Promise<void> {
    setIsApplying(true);
    const errors = await applyToEachPlan(plans, applyToPlan);
    setIsApplying(false);

    trackEvent({
      name: PlanEvents.applyBulkPlanAction,
      props: {
        action,
        planCount: plans.length,
        failedCount: errors.length,
      },
    });

    if (errors.length === 0) {
      openToast({ title: `${planCountText} ${pastParticiple} successfully!` });
    } else {
      handleErrorWithToast({
        id: `bulk-${action}-plans-${Date.now().toString()}`,
        title: `Could not ${infinitive.toLowerCase()} ${errors.length} of ${planCountText}`,
        error: errors[0],
      });
    }

    onApplied();
    onClose();
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title={`${infinitive} ${planCountText}`}
      onConfirm={handleConfirm}
      showSpinner={isApplying}
      confirmText={`Yes, ${infinitive.toLowerCase()}`}
      variant="danger"
      isConfirmDisabled={
        action === "delete"
          ? !isDeletionConfirmed(confirmationText)
          : !deactivationReason
      }
      confirmTooltip={
        action === "delete"
          ? "Type delete to confirm"
          : "A deactivation reason must be entered"
      }
    >
      <Text variant="bodyL">{getConfirmationText(action, plans.length)}</Text>
      <Box component="ul" sx={{ maxHeight: "12rem", overflowY: "auto" }}>
        {plans.map((plan) => (
          <li key={plan.id}>
            <Text variant="bodyM">{formatPlanName(plan)}</Text>
          </li>
        ))}
      </Box>
      {action === "delete" && (
        <DeletePlanConfirmation
          confirmationText={confirmationText}
          onConfirmationTextChange={setConfirmationText}
        />
      )}
      {action === "deactivate" && (
        <TextField
          label="Deactivation reason"
          multiline
          placeholder="Please enter the reason why these plans are being deactivated"
          minRows={2}
          text={deactivationReason}
          onTextChanged={setDeactivationReason}
          variant="outlined"
          fullWidth
          tag="required"
          controlStyle={{ mt: 2 }}
        />
      )}
    </Dialog>
  );
}
